import moment from 'moment'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import LinkButton from 'components/atoms/LinkButton'
import TextClamp from 'components/atoms/TextClamp'
import Typography from 'components/atoms/_layouts/Typography'
import { IColumn } from 'components/organisms/EditTable'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService, reservationService, utilService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import {
  Button,
  Buttons,
  InputTextArea,
  Pagination,
  Panel,
  PanelTitle,
  StatusBadge,
  Toggle,
} from '../../../atoms'
import { getBadgeStateFromWorkflowState } from 'components/atoms/StatusBadge'
import SidebarLayout from '../../_layouts/sidebarLayout'
import { Form } from 'components/molecules'
import EditList from 'components/organisms/EditList'
import { ReservationIndexFilter } from '../parts/ReservationIndexFilter'
import { HostWorkflowOperations } from '../parts/HostWorkflowOperations'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: interfaces.IWindow

interface IProps {
  records: any
  currentUserIsAgent: boolean
  currentUserIsHost: boolean
  isMobile: boolean
  user: any
  host_cancel_reasons: IJsonResponse
  csv_path: string
}

const ReservationsIndex: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle(I18n.t('top_nav.reservations'))
  const { data: user } = utilService.getDataFromJson(props.user)
  const { data: hostCancelReasons } = utilService.getDataFromJson(props.host_cancel_reasons)
  const { tour_reservations: initialRecords } = reservationService.getTourReservationsFromJson(
    props.records.tour_reservations
  )
  const [records, setRecords] = React.useState(initialRecords)
  const [meta, setMeta] = React.useState(props.records.tour_reservations.meta)

  const onChangePageHandler = async page => {
    const currentUrlParams = new URLSearchParams(window.location.search)
    currentUrlParams.set('page', page)
    location.href = `/agent/host_reservations?${currentUrlParams.toString()}`
  }

  const formMemoRef = React.useRef(null)
  const updateMemo = React.useCallback(record => {
    if (!props.currentUserIsAgent) {
      return
    }

    const FIELD = { memo: 'memo' }
    const InputForm: React.FC<{}> = () => {
      const [errors, setErrors] = React.useState({ reason: null })
      const handleEditSubmit = async formValues => {
        if (errors.reason) {
          return
        }

        await agentService.updateReservation(
          { id: record.id, memo: formValues.memo },
          record.class === 'Reservation'
        )
        window.globalModal.closeModal()
        window.location.reload()
      }

      return (
        <Form
          ref={formMemoRef}
          fields={FIELD}
          handleSubmit={(_, values) => handleEditSubmit(values)}
        >
          <InputTextArea
            required={false}
            name="memo"
            label={I18n.t('generic.memo')}
            error={errors.reason}
            defaultValue={record.memo}
          />
        </Form>
      )
    }
    window.globalModal.showModal({
      title: '',
      body: <InputForm />,
      closeText: I18n.t('generic.cancel'),
      submitText: I18n.t('generic.submit'),
      handleSubmit: () => formMemoRef.current.handleFormSubmit(),
    })
  }, [])

  const memoColumn: IColumn = {
    name: I18n.t('generic.memo'),
    field: 'memo',
    fullWidth: true,
    renderElement: ({ reservable }) => (
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', padding: '4px' }}>
        <div style={{ width: 240 }}>{reservable.memo}</div>
        <Button handleClick={() => updateMemo(reservable)} small={true}>
          {`${I18n.t('generic.memo')} ${
            reservable.memo ? I18n.t('generic.edit') : I18n.t('generic.add')
          }`}
        </Button>
      </div>
    ),
  }

  // Column : Guide name, only if current user is an Agent
  const guideNameColumn: IColumn = {
    name: I18n.t('reservation.guide_name'),
    field: 'guide_name',
    required: false,
    readOnly: true,
    renderElement: tour_reservation => {
      return (
        props.currentUserIsAgent && (
          <Typography type="body" size="lg">
            <TextClamp line={2} width={260}>
              {tour_reservation.tour_group?.guide?.username ??
                I18n.t('enums.reservation.assignment_status.unassigned')}
            </TextClamp>
          </Typography>
        )
      )
    },
  }

  const autoThanksColumn: IColumn = {
    name: I18n.t('reservation.guide_name'),
    field: 'auto_thanks',
    required: false,
    readOnly: true,
    renderElement: tour_reservation => {
      const isOwnedTour = tour_reservation.reservable?.post?.user_id === user.id
      const toggleAutoThanks = async () => {
        const {
          tour_reservation: updatedTourReservation,
        } = await reservationService.toggleAutoThanks(tour_reservation.id)
        setRecords(
          records.map(record =>
            record.id === tour_reservation.id
              ? { ...record, auto_thanks: updatedTourReservation.auto_thanks }
              : record
          )
        )
      }
      return (
        isOwnedTour &&
        (tour_reservation.reservable.workflow_state_enum_value === 'approved' ||
          tour_reservation.reservable_type === 'DirectReservation') &&
        !tour_reservation.reservable.thanks_mail_sent_at && (
          <div style={{ gridColumn: 2 }}>
            <Toggle
              label={I18n.t('generic.auto_thanks')}
              name="auto_thanks"
              checked={tour_reservation.auto_thanks}
              onChangeHandler={toggleAutoThanks}
            />
          </div>
        )
      )
    },
  }

  const blankColumn: IColumn = {
    name: '',
    field: '',
    required: false,
    readOnly: true,
  }

  const columns: IColumn[] = React.useMemo(() => {
    const COLUMNS: IColumn[] = [
      {
        name: I18n.t('generic.status'),
        field: 'status',
        required: false,
        readOnly: true,
        renderElement: tour_reservation => {
          const reservation = tour_reservation.reservable
          const type = getBadgeStateFromWorkflowState({
            start_at: reservation.start_at,
            workflow_state_enum_value: reservation.workflow_state_enum_value,
          })
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <StatusBadge label={reservation.workflow_state} type={type} />
              {reservation.has_disputes && (
                <div className="dispute_warning">
                  <SvgIcon name="warning" className="icon" ariaHidden={true} role="presentation" />
                  {I18n.t('generic.dispute')}
                </div>
              )}
            </div>
          )
        },
      },
      {
        name: I18n.t('reservation.reservation_id_and_guest'),
        field: 'created_at',
        required: false,
        readOnly: true,
        renderElement: tour_reservation => {
          const reservation = tour_reservation.reservable
          return (
            <>
              <Typography type="body" size="xs" color="subtext">
                {reservation.no}
              </Typography>
              <Typography type="body">{reservation.guest_name}</Typography>
            </>
          )
        },
      },
      {
        name: I18n.t('post.post_name'),
        field: 'post_name',
        required: false,
        readOnly: true,
        renderElement: tour_reservation => {
          const reservation = tour_reservation.reservable
          return (
            <LinkButton href={`/posts/${reservation.post.slug}`}>
              <Typography type="body" size="lg">
                <div style={{ whiteSpace: 'initial', textAlign: 'initial' }}>
                  {reservation.post.name}
                </div>
              </Typography>
            </LinkButton>
          )
        },
      },
      {
        name: I18n.t('reservation.guest_name'),
        field: 'guest_name',
        required: false,
        readOnly: true,
        renderElement: tour_reservation => (
          <Typography type="body" size="lg">
            <TextClamp line={2} width={260}>
              {tour_reservation.reservable.guest_name || tour_reservation.reservable.user?.fullname}
            </TextClamp>
          </Typography>
        ),
      },
      {
        name: I18n.t('generic.tour_schedule'),
        field: 'start_at',
        required: false,
        readOnly: true,
        renderElement: tour_reservation => {
          const startMoment = moment(tour_reservation.reservable.start_at_raw).locale(I18n.locale)
          return (
            <div className="schedule_wrap">
              {`${startMoment.format(
                I18n.locale === 'ja' ? 'YYYY年MM月DD日(ddd) HH:mm' : 'MMM DD ddd, YYYY HH:mm'
              )}`}
              -{tour_reservation.reservable.end_at}
            </div>
          )
        },
      },
      {
        name: I18n.t('reservation.guest_nb'),
        field: 'guest_nb',
        required: false,
        readOnly: true,
        renderElement: tour_reservation => {
          const reservation = tour_reservation.reservable
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography>
                {I18n.t('generic.nb_of_adults', {
                  nb: reservation.num_of_adults,
                })}
              </Typography>
              <Typography>
                {I18n.t('generic.nb_of_children', {
                  nb: reservation.num_of_children,
                })}
              </Typography>
              <Typography>
                {I18n.t('generic.nb_of_infants', {
                  nb: reservation.num_of_infants,
                })}
              </Typography>
            </div>
          )
        },
      },
    ]
    if (props.currentUserIsAgent) {
      COLUMNS.splice(3, 0, guideNameColumn)
      COLUMNS.splice(COLUMNS.length, 0, memoColumn)
    }
    COLUMNS.push(blankColumn, autoThanksColumn)
    return COLUMNS
  }, [props])
  const csvUrlWithParams = React.useMemo(() => {
    const currentUrlParams = new URLSearchParams(window.location.search)
    currentUrlParams.delete('page')
    return `${props.csv_path}&${currentUrlParams.toString()}`
  }, [props])

  return (
    <SidebarLayout user={user}>
      <ReservationsIndexWrapper>
        <Panel
          marginType="dashboard"
          className="With_Filter"
          title={
            <PanelTitle
              title={I18n.t('post.reservation_confirmation')}
              rightContent={
                props.csv_path ? (
                  <Buttons>
                    <Button small={true}>
                      <a href={csvUrlWithParams}>
                        {I18n.t('generic.csv') + ' ' + I18n.t('generic.download')}
                      </a>
                    </Button>
                  </Buttons>
                ) : null
              }
            />
          }
        >
          <h2>{I18n.t('generic.filter')}</h2>

          <ReservationIndexFilter {...props} currentPagePath="/agent/host_reservations" />
          {records.length === 0 ? (
            <div className="ReservationIndex_NoReservation">
              <SvgIcon
                name="calendar_today"
                className="icon"
                ariaHidden={true}
                role="presentation"
              />
              <h1>{I18n.t('reservation.no_reservations')}</h1>
            </div>
          ) : (
            <EditList
              editable={true}
              columns={columns}
              records={records}
              pagination={null}
              renderRecordControls={record => (
                <HostWorkflowOperations
                  currentRecord={record}
                  hostCancelReasons={hostCancelReasons}
                  setRecords={setRecords}
                  {...props}
                  user={user}
                />
              )}
            />
          )}

          {meta && (
            <Pagination
              onChangePageHandler={onChangePageHandler}
              currentPage={meta.current_page}
              prevPage={meta.prev_page}
              nextPage={meta.next_page}
              totalPages={meta.total_pages}
              totalCount={meta.total_count}
            />
          )}
        </Panel>
      </ReservationsIndexWrapper>
    </SidebarLayout>
  )
}

const ReservationsIndexWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    padding: 20px 0;
  }
  .ReservationIndex_NoReservation {
    display: flex;
    align-items: center;
    flex-direction: column;
    padding-top: 24px;
    > i {
      font-size: 32px;
    }
    > h1 {
      margin-top: 12px;
      font-weight: bold;
      font-size: 20px;
    }
  }
  .ReservationIndex_Header {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 16px;
    > h2 {
      font-size: 14px;
      margin-bottom: 8px;
    }
  }
  .ReservationIndex_Filters {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .Scroller {
    width: 5000px;
  }
  .direct_reservation_hidden {
    display: none;
  }

  .schedule_wrap {
    width: fit-content;
  }

  .Button {
    margin-left: 3px;
  }
  .With_Filter {
    min-height: 600px;
  }

  .dispute_warning {
    display: flex;
    align-items: center;
    gap: 4px;
    color: ${constants.COLORS.danger};
    font-size: 14px;
    font-weight: bold;
    .icon {
      width: 16px;
      height: 16px;
    }
  }
`

export default ReservationsIndex
